import { CopyDuplicateObject } from '@griply/assets';
import { ActionEvents, CalendarEventOccurrenceTarget } from '@griply/types';
import { getConnectionProvider, getExternalEventEditable } from '@griply/utils';
import {
  useCreateCalendarEventAllowed,
  useEditCalendarEventAllowed,
  useExternalCalendarConnections,
} from 'features/external-connections';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PenEdit16, Trash16 } from 'shared/assets/icons';
import { ConfirmDialog } from 'shared/components/ui/confirm-dialog';
import { EventApplyToOccurrencesDialog } from 'shared/components/ui/event-form';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
// the context file, not the barrel — the barrel reaches the provider, which renders this menu
import { ExternalEventMenuProps } from 'shared/contexts/event-form/event-form-context';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useDeleteExternalCalendarEventMutation } from 'shared/hooks/queries/use-delete-external-calendar-event-mutation';
import { useExternalCalendarEventQuery } from 'shared/hooks/queries/use-external-calendar-event-query';
import { useTrackEvents } from 'shared/hooks/use-track-events';

// the prop contract lives on the event-form context, which hands this menu to its ui hosts
export type ExternalEventContextMenuProps = ExternalEventMenuProps;

export const ExternalEventContextMenu: React.FC<
  ExternalEventContextMenuProps
> = ({
  eventTarget,
  isRecurring,
  isOrganizer,
  onEdit,
  onDuplicate,
  onClose,
  onAfterAction,
  ...popupProps
}) => {
  const { t } = useTranslation();
  const track = useTrackEvents();
  const connections = useExternalCalendarConnections();
  const { allowed: editAllowed } = useEditCalendarEventAllowed();
  // a copy is a new event, so it answers to the create limit rather than the edit one
  const { allowed: createAllowed } = useCreateCalendarEventAllowed();
  const openPremiumDialog = useOpenPremiumDialog();
  const { mutate: deleteEvent } = useDeleteExternalCalendarEventMutation();

  // Same gate mobile's detail screen and the event popup apply: an invitee never sees Delete,
  // and editing calendar events is premium.
  const canDelete =
    editAllowed &&
    getExternalEventEditable(eventTarget, { connections, isOrganizer });

  // a false flag means "not known to repeat", so confirm before offering a lone single-delete
  const { details } = useExternalCalendarEventQuery(
    canDelete && !isRecurring ? eventTarget : null,
  );
  const repeats = isRecurring || !!details?.isRecurring;

  const [occurrencesPromptOpen, setOccurrencesPromptOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // hosts dismiss first: PopupMenu's escape stack would otherwise own Escape over the dialog
  const onSelectEdit = () => {
    onClose();
    if (!editAllowed) {
      onAfterAction?.();
      openPremiumDialog();
      return;
    }
    onEdit?.();
  };

  const onSelectDuplicate = () => {
    onClose();
    onAfterAction?.();
    if (!createAllowed) {
      openPremiumDialog();
      return;
    }
    onDuplicate?.();
  };

  const deleteWithOccurrenceTarget = (
    occurrenceTarget: CalendarEventOccurrenceTarget,
  ) => {
    deleteEvent({ ...eventTarget, editScope: occurrenceTarget });
    track(ActionEvents.CalendarEventDelete, {
      provider: getConnectionProvider(connections, eventTarget.connectionId),
      is_recurring: repeats,
      edit_scope: occurrenceTarget,
    });
    onClose();
    onAfterAction?.();
  };

  // a one-off still confirms — mobile's detail screen asks the same question before deleting
  const onSelectDelete = () => {
    if (!repeats) {
      setConfirmDeleteOpen(true);
      return;
    }
    setOccurrencesPromptOpen(true);
  };

  const onConfirmDelete = () => {
    setConfirmDeleteOpen(false);
    deleteWithOccurrenceTarget(CalendarEventOccurrenceTarget.Single);
  };

  // dismissing either question abandons the delete outright — the menu has nothing left to offer
  const onCancelOccurrences = () => {
    setOccurrencesPromptOpen(false);
    onClose();
  };

  const onCancelConfirmDelete = () => {
    setConfirmDeleteOpen(false);
    onClose();
  };

  // the prompts are layered over this menu, so an outside click there must not close it underneath
  const onOutside = () => {
    if (occurrencesPromptOpen || confirmDeleteOpen) {
      return;
    }
    onClose();
  };

  return (
    <PopupMenu {...popupProps} onOutside={onOutside}>
      <PopupMenuList hasBottomBorder>
        {!!onEdit && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onSelectEdit}
              start={<Icon icon={PenEdit16} />}
              isPremium={!editAllowed}
            >
              {t('context-menus.external-event.labels.edit')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
        {!!onDuplicate && (
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onSelectDuplicate}
              start={<Icon icon={CopyDuplicateObject} />}
              isPremium={!createAllowed}
            >
              {t('context-menus.external-event.labels.duplicate')}
            </PopupMenuButton>
          </PopupMenuListItem>
        )}
      </PopupMenuList>
      {canDelete && (
        <PopupMenuList>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onSelectDelete}
              start={<Icon icon={Trash16} />}
              startColor={Colors.Error}
              textColor={Colors.Error}
            >
              {t('context-menus.external-event.labels.delete')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      {occurrencesPromptOpen && (
        <EventApplyToOccurrencesDialog
          onSelect={deleteWithOccurrenceTarget}
          onClose={onCancelOccurrences}
        />
      )}

      {confirmDeleteOpen && (
        <ConfirmDialog
          open
          abovePopups
          title={t('forms.event.delete.confirm.title')}
          message={t('forms.event.delete.confirm.message')}
          onConfirm={onConfirmDelete}
          onCancel={onCancelConfirmDelete}
        />
      )}
    </PopupMenu>
  );
};
